import type { ReactNode } from 'react'
import { FileText } from 'lucide-react'
import { PhotoType, type ClientOngoingLoad } from '@numeo/types-core'
import { reasonsSubline, type ArReadinessReasonCode, type InvoicingStatus, type TmsInvoiceRow, type TmsPodSource } from '@numeo/types-main-web-app'
import { Badge, Button, TextField } from '@/components/ds2'
import { formatDate } from '../../results/format'
import { RevisionReview } from '../../uploads/RevisionReview'
import { UploadButton } from '../../uploads/UploadButton'
import { podIssueSentenceOf, rateConMissingOf } from './reviewModel'
import { ReviewNotice, SectionLabel } from './ReviewNotice'
import type { DrawerUploads } from './useDrawerUploads'

const POD_SOURCE_LABEL: Record<TmsPodSource, string> = {
    telegram: 'Telegram',
    driver_app: 'Mobile app',
    email: 'Connected email',
    manual: 'Manual upload',
}

/** Where the POD came from, in the words the drawer shows. */
const podSourceLabel = (source: TmsPodSource | null | undefined): string => (source ? POD_SOURCE_LABEL[source] : 'Document on file')

/** When the newest POD file of the load was stored, as the drawer shows it ("Oct 26, 2026, 5:00 PM"), or null when unknown. */
const podTimeOf = (load: ClientOngoingLoad | undefined): string | null => {
    const stamps = (load?.photoProofs ?? [])
        .filter((proof) => proof.type === PhotoType.POD && !proof.mergedIntoUrl && proof.timestamp)
        .map((proof) => new Date(proof.timestamp as Date).getTime())
        .filter((time) => Number.isFinite(time))
    if (!stamps.length) return null
    const at = new Date(Math.max(...stamps))
    return `${formatDate(at)}, ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

/**
 * The rate con on file as the drawer names it: the version the load uses (the paperwork's active card, `pdfLink`) and when it came in,
 * when the load files its versions; never a source the load does not record (an uploaded con is not "Connected email").
 */
const rateConSourceOf = (load: ClientOngoingLoad | undefined): string => {
    const versions = load?.rateconVersions ?? []
    const active = versions.find((version) => version.url === load?.pdfLink)
    if (!active) return 'On file'
    const label = versions.length > 1 ? `Version ${active.version} of ${versions.length}` : 'On file'
    return active.receivedAt ? `${label} · ${formatDate(new Date(active.receivedAt))}` : label
}

/** A document that is not on file: v17's words, never a source it does not have. */
const NO_DOCUMENT = 'No matching document available'

interface DocumentRowProps {
    name: string
    source: string
    /** Absent: the row shows no state (a rate con on file is just on file, nothing was matched). */
    state?: 'matched' | 'review' | 'missing' | 'checking'
    action?: ReactNode
    view?: ReactNode
}

/** A document's state as a DS v2 soft badge: green when it matches, amber to review, red when missing (invoicing needs it), grey while checked. */
const STATE_BADGE: Record<NonNullable<DocumentRowProps['state']>, { label: string; type: 'success' | 'warning' | 'danger' | 'default' }> = {
    matched: { label: 'Matched', type: 'success' },
    review: { label: 'Review', type: 'warning' },
    missing: { label: 'Missing', type: 'danger' },
    checking: { label: 'Checking…', type: 'default' },
}

/** One document of the match (v17 `.docrow`): its icon tile, its name and source, its state and the button that acts on it. */
const DocumentRow = ({ name, source, state, action, view }: DocumentRowProps) => (
    <div className="my-2 flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
        <span className="rounded-lg bg-default p-2 text-foreground-muted" aria-hidden>
            <FileText className="h-4 w-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
            <strong className="text-sm font-medium text-foreground">{name}</strong>
            <small className="mt-0.5 text-xs text-foreground-muted">{source}</small>
        </div>
        {state && <Badge variant="soft" type={STATE_BADGE[state].type} size="md" label={STATE_BADGE[state].label} />}
        {view}
        {action}
    </div>
)

interface DocumentsSectionProps {
    row: TmsInvoiceRow
    status: InvoicingStatus
    /** The load as the board stores it: when its POD file came in. */
    load?: ClientOngoingLoad
    loadId: string
    loadNumber: string
    /** The POD was just filed and its check is still running: the row reads again until it finishes. */
    awaitingCheck: boolean
    confirmReason: string
    busy: boolean
    onConfirmReason: (reason: string) => void
    onConfirm: () => void
    /** Opens a document's own view in the drawer: the proof of delivery or the rate confirmation. */
    onView: (view: 'document' | 'rate') => void
    /** The drawer's own uploads: what they file is shown here, never said to the chat. */
    uploads: DrawerUploads
}

/**
 * The row's reasons the drawer still has to say in its own box: those a document notice above already says are left out, so one
 * thing is said once ("Rate con missing" under "A rate confirmation is required…"). Empty when nothing is left.
 */
const otherReasonsLine = (row: Pick<TmsInvoiceRow, 'reasons' | 'warnings'>, said: ReadonlySet<ArReadinessReasonCode>): string => {
    const keep = <T extends { code: ArReadinessReasonCode }>(list: T[] | undefined) => (list ?? []).filter((reason) => !said.has(reason.code))
    return reasonsSubline({ reasons: keep(row.reasons), warnings: keep(row.warnings) })
}

const ViewButton = ({ onClick }: { onClick: () => void }) => (
    <Button size="sm" variant="outline" onClick={onClick}>
        View
    </Button>
)

/**
 * The review drawer's "Matched documents" (v17 openLoad): the rate confirmation, and the proof of delivery with its source and time, its
 * state and the board's mismatch sentence. A POD on file that does not match (Needs review) is confirmed here with a reason; a missing
 * POD is uploaded. A document that is not on file says so, never where it came from.
 */
export const DocumentsSection = (props: DocumentsSectionProps) => {
    const { row, status, load, loadId, loadNumber, awaitingCheck, confirmReason, busy, onConfirmReason, onConfirm, onView, uploads } = props
    const documents = row.documents
    const pod = documents?.pod ?? 'none'
    const issue = podIssueSentenceOf(row)
    const podState: DocumentRowProps['state'] = awaitingCheck
        ? 'checking'
        : pod === 'accepted' || status === 'ready'
          ? 'matched'
          : pod === 'none'
            ? 'missing'
            : 'review'
    const podSource = pod === 'none' ? NO_DOCUMENT : [podSourceLabel(row.pod_source), podTimeOf(load)].filter(Boolean).join(' · ')
    const rateCon = documents?.rate_con === true || uploads.rateConFiled
    // The rate con notice says what is missing; the reason box below does not say it again.
    const rateConNotice = rateConMissingOf(row) && !uploads.rateConFiled && !uploads.revision
    const said = new Set<ArReadinessReasonCode>(rateConNotice || uploads.rateConFiled ? ['missing_ratecon'] : [])
    const otherLine = status === 'other' ? otherReasonsLine(row, said) : ''

    return (
        <section aria-label="Matched documents">
            <SectionLabel>Matched documents</SectionLabel>
            <DocumentRow
                name="Rate confirmation"
                source={rateCon ? rateConSourceOf(load) : NO_DOCUMENT}
                state={rateCon ? undefined : 'missing'}
                view={rateCon ? <ViewButton onClick={() => onView('rate')} /> : undefined}
                action={
                    rateCon || !documents || uploads.revision ? undefined : (
                        <UploadButton
                            loadNumber={loadNumber}
                            loadId={loadId || undefined}
                            type={PhotoType.RATECON}
                            label="Upload rate con"
                            upload={uploads.uploadRatecon}
                        />
                    )
                }
            />
            {uploads.revision && <RevisionReview revision={uploads.revision} onChoice={uploads.revisionChosen} onDone={uploads.closeRevision} />}
            <DocumentRow
                name="Proof of delivery"
                source={podSource}
                state={podState}
                view={pod !== 'none' ? <ViewButton onClick={() => onView('document')} /> : undefined}
                action={
                    pod === 'none' && !awaitingCheck ? (
                        <UploadButton loadNumber={loadNumber} loadId={loadId || undefined} type={PhotoType.POD} label="Upload POD" upload={uploads.uploadPod} />
                    ) : undefined
                }
            />
            {uploads.notice && <ReviewNotice tone="warning">{uploads.notice}</ReviewNotice>}
            {rateConNotice && (
                <ReviewNotice tone="warning">A rate confirmation is required before the invoice can be created. Upload one to continue.</ReviewNotice>
            )}
            {status === 'needs_review' && (
                <>
                    <ReviewNotice tone="warning">
                        {issue ?? 'The POD on file does not match this delivery.'} Confirm the document belongs to this delivery before proceeding.
                    </ReviewNotice>
                    <TextField
                        label="Reason for confirming this match"
                        placeholder="e.g. Verified receiver, route and delivery signature"
                        value={confirmReason}
                        onChange={(event) => onConfirmReason(event.target.value)}
                    />
                    <div className="mt-2.5">
                        <Button size="sm" variant="secondary" loading={busy} onClick={onConfirm}>
                            Confirm document match
                        </Button>
                    </div>
                </>
            )}
            {status === 'missing_pod' && !awaitingCheck && (
                <ReviewNotice tone="warning">
                    A signed POD is required before the invoice can be created. Upload one to attach it for manual review.
                </ReviewNotice>
            )}
            {status === 'ready' && (
                <ReviewNotice tone="success">✓ Load reference, broker, delivery and rate match. No existing invoice was found.</ReviewNotice>
            )}
            {otherLine && <ReviewNotice tone="warning">{otherLine}</ReviewNotice>}
        </section>
    )
}
